/**
 * MSAL / Entra bootstrap for the unified shell.
 *
 * MSAL is the ONLY place that knows about Entra. The @andavo/web-ui/api
 * configureApi wrapper never imports MSAL — it only receives getAccessToken /
 * clearCredentials callbacks (see ./resolver.ts). At merge into andavo-dev,
 * swapping Entra → Auth0 means replacing this file + resolver.ts and nothing
 * else (DO NOT copy andavo-dev's Auth0 — Auth0 is the merge TARGET, labs is
 * Entra/MSAL).
 *
 * Ported from apps/experts-web/src/auth/msal.ts (the verified, prod-hardened
 * template — the navigateToLoginRequestUrl:false fix from the 2026-06-09 outage,
 * the env-gated E2E test-mode bearer seam, the Sentry anomaly capture). THE ONE
 * STRUCTURAL CHANGE: acquireToken is SCOPE-PARAMETERIZED — acquireToken(scopes)
 * backed by acquireTokenSilent({scopes}). experts-web used a single module-level
 * `entraScopes`; the unified shell serves modules whose backends need DIFFERENT
 * audiences (experts vs crm), so each call passes ITS resource's scopes. The
 * registration that mints the token is resolved per-scope (clientIdForScopes),
 * supporting either ONE SPA registration or a per-resource map — config-driven,
 * no rework when the Entra topology is confirmed (open_decision #1).
 */
import {
  type AccountInfo,
  type Configuration,
  ProtocolMode,
  PublicClientApplication,
} from '@azure/msal-browser';
import { captureAuthAnomaly } from '@/observability/sentry';
import { type AuthConfig, clientIdForScopes } from './config';

let msalInstance: PublicClientApplication | null = null;
let authConfig: AuthConfig | null = null;
let activeAccount: AccountInfo | null = null;

// ── E2E test-mode state (NON-PROD ONLY — see maybeBootstrapTestMode) ──────────
// When the e2e harness injects a real (or, in headless smoke, a synthetic) Entra
// bearer into localStorage, the SPA sources its bearer from here instead of from
// MSAL. This is purely the SPA's choice of WHERE to read the bearer — there is
// NO server-side trust shortcut (a real backend still validates the token).
let testModeBearer: string | null = null;

/** localStorage keys the e2e auth.setup writes (namespaced; real users never set them). */
const E2E_BEARER_KEY = '__andavo_web_e2e_bearer';
const E2E_ACCOUNT_KEY = '__andavo_web_e2e_account';

/**
 * E2E test-mode bootstrap — NON-PROD ONLY.
 *
 * SAFETY BOUNDARY (mirrors experts-web maybeBootstrapTestMode):
 *   Activates ONLY when ALL hold:
 *     1. localStorage.__andavo_web_e2e_bearer AND .__andavo_web_e2e_account set.
 *     2. The resolved environment is on the NON-PROD allow-list. The env signal
 *        is build/server-provided, not client-settable.
 *   FAIL SAFE (off): missing/empty/unrecognised env, unreadable storage, or
 *   malformed account JSON ⇒ do NOT activate; fall through to real MSAL.
 *   In production we best-effort CLEAR any stray keys (never throw).
 *
 * When active: set the module-level activeAccount (so ProtectedRoute passes) and
 * stash the bearer so acquireToken() hands it out — as if interactive sign-in had
 * populated the MSAL cache. Real users' loginRedirect / PKCE callback /
 * logoutRedirect paths are UNCHANGED.
 */
function maybeBootstrapTestMode(config: AuthConfig): boolean {
  const NONPROD_ENVS = new Set(['dev', 'stg', 'staging', 'local', 'test']);
  const env = (config.environment ?? '').toLowerCase();
  const isNonProd = NONPROD_ENVS.has(env);

  let bearer: string | null = null;
  let accountJson: string | null = null;
  try {
    bearer = localStorage.getItem(E2E_BEARER_KEY);
    accountJson = localStorage.getItem(E2E_ACCOUNT_KEY);
  } catch {
    // localStorage blocked (Safari ITP, private mode) — never activate.
    return false;
  }

  if (!isNonProd) {
    // Production (or any non-allow-listed env): hard refusal. Best-effort clear
    // stray injected keys so they cannot influence a later load. Never throw.
    if (bearer || accountJson) {
      try {
        localStorage.removeItem(E2E_BEARER_KEY);
        localStorage.removeItem(E2E_ACCOUNT_KEY);
      } catch {
        /* ignore storage errors */
      }
    }
    return false;
  }

  if (!bearer || !accountJson) return false;

  let account: AccountInfo;
  try {
    account = JSON.parse(accountJson) as AccountInfo;
  } catch {
    return false;
  }
  // Minimal shape check — a real-shaped MSAL AccountInfo has these.
  if (!account || !account.username || !account.localAccountId) return false;

  testModeBearer = bearer;
  activeAccount = account;
  // Observability tag (not a security control): lets specs/humans confirm the
  // synthetic seam is active via DevTools.
  try {
    (
      window as unknown as { __andavoWebE2ETestModeActive?: boolean }
    ).__andavoWebE2ETestModeActive = true;
  } catch {
    /* best-effort only */
  }
  return true;
}

/**
 * Initialise MSAL from the resolved config and process any redirect response.
 * Idempotent — safe to call once at app bootstrap.
 */
export async function initMsal(config: AuthConfig): Promise<void> {
  if (msalInstance) return;
  if (config.authProvider !== 'entra') {
    throw new Error(
      `Unsupported authProvider "${config.authProvider}" — the shell expects "entra".`,
    );
  }

  authConfig = config;

  // E2E test-mode bootstrap (NON-PROD ONLY). When active, skip building a
  // PublicClientApplication entirely (no redirect, no MSAL cache) and land
  // authenticated via the module-level activeAccount it set.
  if (maybeBootstrapTestMode(config)) {
    return;
  }

  const msalConfig: Configuration = {
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      // LAYER 2 of the 2026-05-02 MSAL P1 (docs/runbooks/incident-response/
      // 2026-05-02-msal-eight-layers.md): protocolMode MUST be OIDC for Entra
      // External ID / CIAM. The default is ProtocolMode.AAD, which drives MSAL
      // to the AAD instance-discovery + endpoint path — CIAM has no such
      // endpoint, so sign-in throws endpoints_resolution_error /
      // openid_config_error, bricking every login. OIDC makes MSAL read the
      // authority's standard .well-known/openid-configuration directly (the only
      // thing CIAM serves).
      //
      // msal-browser v5 NOTE: buildConfiguration() still reads auth.protocolMode
      // at runtime (defaults it to ProtocolMode.AAD then spreads userInputAuth
      // over the defaults — see dist/config/Configuration.mjs), but v5 dropped
      // `protocolMode` from the PUBLIC BrowserAuthOptions TS type (it now lives
      // on the internal options). So we set it on a typed-augmented auth object:
      // runtime-correct, and the augmentation documents the known v5 type gap.
      protocolMode: ProtocolMode.OIDC,
      // REQUIRED for Entra External ID on a custom CIAM host: MSAL only trusts
      // *.microsoftonline.com authorities by default and throws
      // `untrusted_authority` for any other host — bricking login before it
      // reaches the IdP. knownAuthorities whitelists the CIAM host. NOTE
      // (LAYER 1 of the same P1): this host MUST equal the token ISSUER host —
      // the GUID subdomain (<tenantId>.ciamlogin.com), NOT the vanity host —
      // or MSAL's issuer/endpoint validation fails. Both come from config (the
      // authority + knownAuthorities are the GUID-subdomain in .env.example).
      knownAuthorities: config.knownAuthorities,
      redirectUri: config.redirectUri,
      postLogoutRedirectUri: config.postLogoutRedirectUri,
      // navigateToLoginRequestUrl moved to handleRedirectPromise() in msal v5;
      // the `false` we depend on (2026-06-09 outage fix) is passed there.
    } as Configuration['auth'] & { protocolMode: ProtocolMode },
    cache: {
      // localStorage so the session survives the redirect round-trip + reloads.
      cacheLocation: 'localStorage',
    },
  };

  const instance = new PublicClientApplication(msalConfig);
  await instance.initialize();

  // Complete a redirect login if we just came back from Entra.
  //
  // navigateToLoginRequestUrl MUST be false: the app owns post-callback
  // navigation (App.tsx routes /auth/callback → "/"). With `true` MSAL ALSO
  // force-navigates from inside handleRedirectPromise, racing the app's redirect
  // — the account never gets established and the user silently bounces back to
  // sign-in (the 2026-06-09 prod login outage). No exception is thrown, so it is
  // invisible without the Sentry capture below.
  const redirectResult = await instance.handleRedirectPromise({
    navigateToLoginRequestUrl: false,
  });
  if (redirectResult?.account) {
    activeAccount = redirectResult.account;
    instance.setActiveAccount(activeAccount);
  } else {
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      activeAccount = accounts[0];
      instance.setActiveAccount(activeAccount);
    }
  }

  // OBSERVABILITY: if we appear to be returning from the Entra redirect but ended
  // up with NO active account, that's the silent login bounce — capture it.
  if (!activeAccount) {
    const href = typeof window !== 'undefined' ? window.location.href : '';
    const looksLikeCallback =
      href.includes('/auth/callback') ||
      href.includes('code=') ||
      href.includes('error=') ||
      (typeof window !== 'undefined' && window.location.hash.includes('code='));
    if (looksLikeCallback) {
      captureAuthAnomaly(
        'returned from Entra redirect but no account was established',
        {
          url: href.split('?')[0].split('#')[0], // drop code/token — never log the secret
          hadCodeParam: href.includes('code='),
          hadErrorParam: href.includes('error='),
          redirectResultPresent: Boolean(redirectResult),
          accountsInCache: instance.getAllAccounts().length,
          authority: config.authority,
        },
      );
    }
  }

  msalInstance = instance;
}

export function isAuthenticated(): boolean {
  return Boolean(activeAccount);
}

export function getActiveAccount(): AccountInfo | null {
  return activeAccount;
}

function requireInstance(): PublicClientApplication {
  if (!msalInstance) {
    throw new Error('MSAL not initialised — call initMsal() first.');
  }
  return msalInstance;
}

function requireConfig(): AuthConfig {
  if (!authConfig) {
    throw new Error('Auth config not loaded — call initMsal() first.');
  }
  return authConfig;
}

/**
 * Begin an interactive login (full-page redirect). Navigates away.
 *
 * Requests the full configured scope set (the union across modules) so a single
 * sign-in consents to every resource the shell will call. Per-call token
 * minting still narrows to the requested resource's scope via acquireToken().
 */
/**
 * True when the last silent token acquisition failed because the user has to
 * interact again — in practice almost always `refresh_token_expired`.
 *
 * Deliberately a flag read by the data layer rather than an AUTOMATIC redirect.
 * `acquireTokenRedirect` navigates the whole page away and back, which would
 * silently destroy a part-filled form — and this app now has a ~20-field
 * agreement form. So the app SURFACES the need and lets the user click, instead
 * of deciding for them at the exact moment they are typing.
 *
 * Set on failure, cleared on success, so it always describes the current state
 * rather than accumulating.
 */
let interactionRequired = false;

export function needsInteractiveSignIn(): boolean {
  return interactionRequired;
}

/** MSAL error shapes that mean "the user must sign in again". */
function isInteractionRequired(err: unknown): boolean {
  const e = err as { name?: string; errorCode?: string };
  if (e?.name === 'InteractionRequiredAuthError') return true;
  return [
    'refresh_token_expired',
    'interaction_required',
    'consent_required',
    'login_required',
    'no_tokens_found',
    'no_account_error',
  ].includes(e?.errorCode ?? '');
}

/**
 * Re-acquire tokens interactively for the CURRENT account.
 *
 * Uses redirect to match `login()` — the app is a redirect-flow SPA, and mixing
 * in a popup here would be blocked by default in some browsers and behave
 * differently from every other sign-in in the product.
 */
export async function reauthenticate(scopes?: string[]): Promise<void> {
  const config = requireConfig();
  const requested = scopes && scopes.length > 0 ? scopes : config.scopes;
  const instance = requireInstance();
  if (activeAccount) {
    await instance.acquireTokenRedirect({
      scopes: requested,
      account: activeAccount,
    });
    return;
  }
  // No account cached at all — a full sign-in is the only option.
  await instance.loginRedirect({ scopes: config.scopes });
}

export async function login(): Promise<void> {
  const config = requireConfig();
  await requireInstance().loginRedirect({ scopes: config.scopes });
}

/** Clear the local session and redirect to the post-logout URI. */
export async function logout(): Promise<void> {
  // Test-mode seam (NON-PROD only): there is no real MSAL instance to log out
  // of. Clear the injected keys + synthetic account, then navigate to the
  // configured post-logout URI (the registered /signin) so the SPA returns to
  // the signed-out state.
  if (testModeBearer) {
    const postLogout = authConfig?.postLogoutRedirectUri;
    testModeBearer = null;
    activeAccount = null;
    try {
      localStorage.removeItem(E2E_BEARER_KEY);
      localStorage.removeItem(E2E_ACCOUNT_KEY);
    } catch {
      /* ignore storage errors */
    }
    try {
      // Navigate to the configured post-logout URI (placeholder
      // app.andavolabs.com/signin); falls back to the SPA's own /signin origin.
      window.location.href = postLogout || `${window.location.origin}/signin`;
    } catch {
      /* best-effort */
    }
    return;
  }

  const instance = requireInstance();
  activeAccount = null;
  // Uses the registered postLogoutRedirectUri from msalConfig.auth (the
  // configured /signin). Real production logout is unaffected by the test seam.
  await instance.logoutRedirect();
}

/**
 * Acquire an access token silently for the GIVEN scopes.
 *
 * SCOPE-PARAMETERIZED (the auth_unification correction): a chat call passes the
 * chat scope, a CRM call passes the CRM scope — each mints a token with the
 * correct audience. The minting registration is resolved per-scope
 * (clientIdForScopes): single SPA registration, or a per-resource map when the
 * topology needs it. Returns undefined on no account / silent failure (the
 * resolver treats undefined as "no token" without forcing a logout).
 *
 * @param scopes the resource scopes for THIS request (defaults to the full
 *   configured set so a bare call still works).
 */
export async function acquireToken(
  scopes?: string[],
): Promise<string | undefined> {
  const config = authConfig;
  const requested =
    scopes && scopes.length > 0 ? scopes : (config?.scopes ?? []);

  // Test-mode seam: hand out the injected bearer. Only ever set in NON-PROD.
  if (testModeBearer && activeAccount) {
    return testModeBearer;
  }
  // Report WHICH precondition failed rather than returning a bare undefined.
  // All three used to collapse into the same silent `undefined`, and apiFetch
  // then sent a tokenless request whose 401 read "Missing or malformed
  // Authorization header" — three layers away from the cause, with nothing
  // logged anywhere. That is how a whole-CRM-surface 401 sat undiagnosed in
  // staging AND prod on 2026-07-31.
  if (!msalInstance || !activeAccount || !config) {
    captureAuthAnomaly('acquireToken called with MSAL not ready', {
      hasInstance: Boolean(msalInstance),
      hasActiveAccount: Boolean(activeAccount),
      hasConfig: Boolean(config),
      // Scopes are build-time public values (VITE_CRM_SCOPES), not secrets.
      scopes: requested,
    });
    return undefined;
  }
  try {
    // Resolve which registration mints this resource's token (throws loudly if a
    // single call spans multiple registrations — wrong-audience guard).
    clientIdForScopes(config, requested);
    const result = await msalInstance.acquireTokenSilent({
      scopes: requested,
      account: activeAccount,
    });
    interactionRequired = false;
    return result.accessToken;
  } catch (err) {
    // Deliberately still returns undefined — this change adds REPORTING, not new
    // behaviour. An interactive fallback for InteractionRequiredAuthError is the
    // likely next step, but it redirects the user, and adding it before the cause
    // is known would paper over whatever is actually wrong.
    //
    // NEVER log `err` wholesale: an MSAL error can carry token material in its
    // response payload. Only the classifying fields are recorded.
    const e = err as {
      name?: string;
      errorCode?: string;
      subError?: string;
      message?: string;
    };
    // Recorded BEFORE the report so the data layer can distinguish "sign in
    // again" from a genuine failure, and offer the right thing.
    interactionRequired = isInteractionRequired(err);
    captureAuthAnomaly('silent token acquisition failed', {
      scopes: requested,
      interactionRequired,
      errorName: e?.name ?? null,
      errorCode: e?.errorCode ?? null,
      subError: e?.subError ?? null,
      // MSAL messages are diagnostic strings, not credentials.
      message: e?.message ?? null,
    });
    return undefined;
  }
}
